import { type ComponentType } from 'react';
import { useTranslation } from 'react-i18next';
import {
  flexRender,
  type Row,
  Table as TanstackTable,
} from '@tanstack/react-table';

import type { ExpandableRowProps } from '@/components/blocks/data-table/expandable-row';
import { Pagination } from '@/components/blocks/data-table/pagination';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { cn } from '@/lib/utils';

interface DataTableProps<TData> {
  table: TanstackTable<TData>;
  empty?: string;
  isNested?: boolean;
  Expansion?: ComponentType<ExpandableRowProps<TData>>;
  onRowClick?: (row: Row<TData>) => void;
}

export const DataTable = <TData,>({
  table,
  empty,
  isNested = false,
  // eslint-disable-next-line @typescript-eslint/naming-convention
  Expansion,
  onRowClick,
}: DataTableProps<TData>) => {
  const { t } = useTranslation();
  const columns = table.getAllColumns();

  return (
    <div className='flex flex-col gap-4'>
      <div
        className={cn({
          'rounded-md border': !isNested,
        })}>
        <Table>
          <TableHeader>
            {table.getHeaderGroups().map((headerGroup) => (
              <TableRow key={headerGroup.id}>
                {headerGroup.headers.map((header) => (
                  <TableHead key={header.id}>
                    {header.isPlaceholder
                      ? null
                      : flexRender(
                          header.column.columnDef.header,
                          header.getContext()
                        )}
                  </TableHead>
                ))}
              </TableRow>
            ))}
          </TableHeader>
          <TableBody>
            {table.getRowModel().rows.length ? (
              table.getRowModel().rows.map((row) => (
                <>
                  <TableRow
                    key={row.id}
                    data-state={row.getIsSelected() && 'selected'}
                    onClick={() => onRowClick?.(row)}>
                    {row.getVisibleCells().map((cell) => (
                      <TableCell key={cell.id}>
                        {flexRender(
                          cell.column.columnDef.cell,
                          cell.getContext()
                        )}
                      </TableCell>
                    ))}
                  </TableRow>
                  {row.getIsExpanded() && Expansion && (
                    <TableRow>
                      <td colSpan={columns.length}>
                        <Expansion row={row} />
                      </td>
                    </TableRow>
                  )}
                </>
              ))
            ) : (
              <TableRow>
                <TableCell
                  colSpan={columns.length}
                  className='h-24 text-center'>
                  {empty ?? t('blocks.dataTable.noResults')}
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </div>
      {!isNested && <Pagination table={table} />}
    </div>
  );
};
